import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { FormLabelWithCount } from './FormLabel/FormLabelWithCount';
import { ReactSVG } from 'react-svg';
import { Upload } from 'ck-assets';
import clsx from 'clsx';

interface FormInputProps<T extends FieldValues> {
  name: Path<T>;
  parentForm: UseFormReturn<T>;
  accept: React.InputHTMLAttributes<HTMLInputElement>['accept'];
  emptyState?: string;
  description?: string;
  label?: string;
}

export const UploadInput = <T extends FieldValues>({
  name,
  parentForm: form,
  description,
  accept,
  emptyState,
  label,
}: FormInputProps<T>) => {
  const { control, watch } = form;
  const selectedFile = watch(name) as File | undefined;

  const maxUploadSize = 'Max 4mb.';
  const inputId = `file-input-${String(name)}`;

  return (
    <label htmlFor={inputId} className='z-0 mb-6'>
      {label && (
        <FormLabelWithCount
          name={name}
          label={label}
          description={`${description} ${maxUploadSize}`}
        />
      )}

      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <input
            id={inputId}
            type='file'
            accept={accept}
            className='absolute h-0 w-0 opacity-0'
            // IMPORTANT: don't pass a value prop for type="file"
            name={field.name}
            ref={field.ref}
            onBlur={field.onBlur}
            onChange={(e) => {
              const file = e.target.files?.[0];
              console.log('file changed ', e.target.files?.[0]);
              if (!file) {
                console.log('No file');
                return;
              }

              const fourMegaBytes = 4 * 1024 * 1024;
              if (file.size >= fourMegaBytes) {
                alert(`För stor bild. ${maxUploadSize}`);
                e.currentTarget.value = '';
                return;
              }

              const filenameWithoutEnding = file.name.substring(
                0,
                file.name.lastIndexOf('.')
              );

              if (filenameWithoutEnding.includes('.')) {
                alert('Filnamnet får inte innehålla punkter.');
                e.currentTarget.value = '';
                return;
              }

              field.onChange(file);

              // Allow re-selecting the same file name next time
              e.currentTarget.value = '';
            }}
          />
        )}
      />

      <div
        className={clsx(
          'text-md mb-6 flex items-center justify-between rounded border border-gray-300 bg-white py-4 px-4 font-light text-gray-400 hover:cursor-pointer hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 active:bg-white',
          selectedFile?.name && 'text-black'
        )}
      >
        {selectedFile?.name || emptyState || 'Välj fil'}
        <ReactSVG src={Upload} className='ml-2 h-6 w-6 text-black' />
      </div>
    </label>
  );
};
